import {FC, createContext, createRef, useContext, useMemo} from 'react';
import {useQueue} from '@mantine/hooks';
import {v4 as uuidv4} from 'uuid';

import NotificationList from './parts/NotificationList';
import {InternalNotification, Notification} from './types';

interface NotificationsContextValue {
  showNotification: (notification: Notification) => void;
  hasNotifications: boolean;
}

const NotificationQueueContext = createContext<NotificationsContextValue | undefined>(undefined);

export const useNotifications = () => {
  const queue = useContext(NotificationQueueContext);

  if (!queue) {
    throw new Error('Missing <NotificationProvider>');
  }
  return queue;
};

export const NotificationProvider: FC<any> = ({children, limit = 3}) => {
  const queue = useQueue({
    initialValues: [] as InternalNotification[],
    limit,
  });

  const handleCloseNotification = (id: string) => {
    queue.update((notifications: InternalNotification[]) =>
      notifications.filter((n) => n.id !== id)
    );
  };

  const showNotification = (notification: Notification) => {
    const id = uuidv4();

    const internalNotification: InternalNotification = {
      ...notification,
      id,
      ref: createRef(),
    };

    queue.add(internalNotification);
  };

  const notificationApi = useMemo(
    () => ({
      showNotification,
      hasNotifications: Boolean(queue.state.length),
    }),
    [queue.state.length]
  );

  return (
    <NotificationQueueContext.Provider value={notificationApi}>
      {children}
      <NotificationList notifications={queue.state} onCloseNotification={handleCloseNotification} />
    </NotificationQueueContext.Provider>
  );
};
